import type { AnalysisEvidence } from '@/code/core/services/analytics/analysisQuery';
import {
  CONVERSATION_DATE_FIELDS,
  type ConversationDateRange,
  dateFromParam,
  dateToParam,
} from '@/code/lib/conversationFilters';

/** Params for the bulk conversation viewer (`/employee/conversations`). */
export interface EmployeeConversationsParams {
  /** Exact analytics membership; resolved server-side, never a serialized ID list. */
  analysis?: AnalysisEvidence;
  /** Explicit lead IDs to load (takes precedence over scope filters). */
  leadIds?: string[];
  campaignIds?: string[];
  orgIds?: string[];
  userIds?: string[];
  /** Lead stage(s) — IN filter, e.g. ["REPLIED", "CALL_BOOKED"]. */
  stages?: string[];
  /** Campaign stage(s) — IN filter, e.g. ["LAUNCHED", "SUNSETTING"]. */
  campaignStages?: string[];
  /** Response stage(s) — IN filter, e.g. ["IN_PROGRESS", "HUMAN_FLAG"]. */
  responseStages?: string[];
  /**
   * @deprecated Use `dateRanges` with field "updated". Kept so existing links
   * (and MCP tools passing since/until) still resolve.
   */
  since?: string;
  /** @deprecated Use `dateRanges` with field "updated". */
  until?: string;
  /** Per-field date windows (queued at, call booked at, created, …). */
  dateRanges?: ConversationDateRange[];
  /** Pre-select this lead's conversation in the detail panel. */
  leadId?: string;
}

/** Params for the employee readout crosstab (`/employee/readout`). */
export interface EmployeeReadoutParams {
  /** Customer org to load. Omit for the empty "pick a customer" state. */
  organizationId?: string;
  /** Which tab; defaults to "data" so it's omitted from the URL. */
  tab?: 'data' | 'slack';
  /** A saved view to apply on open (see ReadoutView). */
  viewId?: string;
}

export class LinkRouter {
  static routeForHelp(slug: string, path = '', preview = false): string {
    return `${preview ? LinkRouter.routeForEmployeeHelpCenter() : '/help'}/${[slug, ...path.split('/').filter(Boolean)].map(encodeURIComponent).join('/')}`;
  }

  static absoluteHelpUrl(path: string, previewHost?: string): string {
    if (process.env.VERCEL_ENV === 'preview') {
      if (process.env.DEPLOYMENT_TYPE === 'employee') {
        if (!process.env.APP_BASE_URL)
          throw new Error('The employee preview needs its matching app URL.');
        return LinkRouter.absoluteAppUrl(path);
      }
      const host = previewHost || process.env.VERCEL_URL;
      if (!host) throw new Error('The preview deployment URL is unavailable.');
      return new URL(path, `https://${host}`).toString();
    }
    return LinkRouter.absoluteAppUrl(path);
  }

  static routeForEmployeeHelpCenter(): string {
    return '/employee/help-center';
  }

  // ---------------------------------------------------------------------------
  // Base URL Resolution (private)
  // ---------------------------------------------------------------------------

  private static getAppBaseUrl(): string {
    return (
      process.env.APP_BASE_URL ||
      (process.env.NODE_ENV === 'production'
        ? 'https://app.aurium.ai'
        : 'http://localhost:3000')
    );
  }

  private static getEmployeeBaseUrl(): string {
    return (
      process.env.EMPLOYEE_BASE_URL ||
      (process.env.NODE_ENV === 'production'
        ? 'https://employee.aurium.ai'
        : 'http://localhost:3000')
    );
  }

  private static buildAbsolute(
    base: string,
    path: string,
    params?: Record<string, string>,
  ): string {
    const url = new URL(path, base);
    if (params) {
      const searchParams = new URLSearchParams(params);
      url.search = searchParams.toString();
    }
    return url.toString();
  }

  // ---------------------------------------------------------------------------
  // Absolute URL Builders (for emails, Slack messages, external redirects)
  // ---------------------------------------------------------------------------

  static absoluteAppUrl(path: string, params?: Record<string, string>): string {
    return LinkRouter.buildAbsolute(LinkRouter.getAppBaseUrl(), path, params);
  }

  static absoluteEmployeeUrl(
    path: string,
    params?: Record<string, string>,
  ): string {
    return LinkRouter.buildAbsolute(
      LinkRouter.getEmployeeBaseUrl(),
      path,
      params,
    );
  }

  /**
   * @deprecated Misnamed — points at `/employee/messaging-inbox`, NOT
   * `/employee/individual-chat`. Use `absoluteEmployeeMessagingInbox` for
   * the messaging-inbox view, or `absoluteEmployeeIndividualChat` for the
   * lighter individual-chat view. Kept for backward compat.
   */
  static absoluteEmployeeChat(leadId: string): string {
    return LinkRouter.absoluteEmployeeMessagingInbox(leadId);
  }

  static absoluteEmployeeMessagingInbox(leadId: string): string {
    return LinkRouter.absoluteEmployeeUrl(
      LinkRouter.routeForEmployeeMessagingInbox(leadId),
    );
  }

  static absoluteEmployeeIndividualChat(leadId: string): string {
    return LinkRouter.absoluteEmployeeUrl(
      LinkRouter.routeForEmployeeIndividualChat(leadId),
    );
  }

  /**
   * Absolute link to the bulk conversation viewer. Hand this to any Aurium
   * MCP tool / Slack message so a human can click in and read a whole set of
   * lead conversations at once. See `routeForEmployeeConversations`.
   */
  static absoluteEmployeeConversations(
    params: EmployeeConversationsParams,
  ): string {
    return LinkRouter.absoluteEmployeeUrl(
      LinkRouter.routeForEmployeeConversations(params),
    );
  }

  /**
   * Relative link to a specific customer's readout (optionally a tab / saved
   * view). This is what makes a readout addressable: an MCP tool or Slack post
   * can hand back a URL that opens the exact org + view, instead of "go to the
   * readout and pick the customer yourself".
   */
  static routeForEmployeeReadout(params: EmployeeReadoutParams = {}): string {
    const search = new URLSearchParams();
    if (params.organizationId) search.set('org', params.organizationId);
    if (params.tab && params.tab !== 'data') search.set('tab', params.tab);
    if (params.viewId) search.set('view', params.viewId);
    const qs = search.toString();
    return `/employee/readout${qs ? `?${qs}` : ''}`;
  }

  static routeForEmployeeAnalysisExport(): string {
    return '/api/employee/analytics/export';
  }

  static routeForEmployeePerformanceDebugger(
    params: Record<string, string> = {},
  ): string {
    const query = new URLSearchParams(params).toString();
    return `/employee/dashboards/performance-debugger${query ? `?${query}` : ''}`;
  }

  /** Absolute form of {@link routeForEmployeeReadout} for emails / Slack / MCP. */
  static absoluteEmployeeReadout(params?: EmployeeReadoutParams): string {
    return LinkRouter.absoluteEmployeeUrl(
      LinkRouter.routeForEmployeeReadout(params),
    );
  }

  static absoluteEmployeeDashboard(
    userId: string,
    campaignId?: string,
    leadId?: string,
  ): string {
    return LinkRouter.absoluteEmployeeUrl(
      LinkRouter.routeForOtherDashboard(userId, campaignId, leadId),
    );
  }

  static absoluteDashboard(campaignId?: string, leadId?: string): string {
    return LinkRouter.absoluteAppUrl(
      LinkRouter.routeForDashboard(campaignId, leadId),
    );
  }

  static absoluteOrganizationInbox(
    organizationSlug: string,
    filters?: Parameters<typeof LinkRouter.routeForOrganizationInbox>[1],
  ): string {
    return LinkRouter.absoluteAppUrl(
      LinkRouter.routeForOrganizationInbox(organizationSlug, filters),
    );
  }

  static absoluteLaunching(campaignId: string): string {
    return LinkRouter.absoluteAppUrl(LinkRouter.routeForLaunching(campaignId));
  }

  static absoluteReverify(): string {
    return LinkRouter.absoluteAppUrl(LinkRouter.routeForReverify());
  }

  // ---------------------------------------------------------------------------
  // Marketing Site URLs (always absolute — different domain)
  // ---------------------------------------------------------------------------

  static routeForPrivacyPolicy(): string {
    return 'https://aurium.ai/privacy';
  }

  static routeForTermsOfService(): string {
    return 'https://aurium.ai/terms';
  }

  static routeForReferral(referralCode: string): string {
    const params = new URLSearchParams({ refer: referralCode });
    return `https://aurium.ai/?${params.toString()}`;
  }

  // ---------------------------------------------------------------------------
  // General App Routes (relative paths for frontend navigation)
  // ---------------------------------------------------------------------------

  static routeForRoot(): string {
    return '/';
  }

  static routeForReverify(): string {
    return '/reverify';
  }

  static routeForPostManager(): string {
    return this.routeForUserPostManager();
  }

  static routeForUserPostManager(): string {
    return '/postManager';
  }

  static routeForNotificationSettings(): string {
    return '/settings/notifications';
  }

  static routeForUserSettings(): string {
    return '/settings/configuration';
  }

  static routeForVerifyCalendarConnection(): string {
    return '/verify-calendar-connection';
  }

  static routeForDashboard(campaignId?: string, leadId?: string): string {
    const params = new URLSearchParams();
    if (campaignId) params.set('campaignId', campaignId);
    if (leadId) params.set('leadId', leadId);
    const qs = params.toString();
    return qs ? `/dashboard?${qs}` : '/dashboard';
  }

  static routeForTasks(taskId?: string): string {
    if (!taskId) return '/tasks';
    const params = new URLSearchParams({ taskId });
    return `/tasks?${params.toString()}`;
  }

  /** Email tasks now render inside the unified /tasks page. */
  static routeForEmailTasks(taskId?: string): string {
    return LinkRouter.routeForTasks(taskId);
  }

  static routeForOtherDashboard(
    userId: string,
    campaignId?: string,
    leadId?: string,
  ): string {
    const params = new URLSearchParams();
    if (campaignId) params.set('campaignId', campaignId);
    if (leadId) params.set('leadId', leadId);
    const qs = params.toString();
    return qs
      ? `/memberDashboard/${userId}/?${qs}`
      : `/memberDashboard/${userId}/`;
  }

  static routeForDataEntry(duplicatingCampaignId?: string): string {
    if (!duplicatingCampaignId) return '/entry';
    const params = new URLSearchParams({ duplicatingCampaignId });
    return `/entry?${params.toString()}`;
  }

  static routeForPay(): string {
    return '/pay';
  }

  static routeForLinkedinOnboarding(): string {
    return '/linkedinAuth';
  }

  static routeForTerms(): string {
    return '/terms';
  }

  // ---------------------------------------------------------------------------
  // Campaign Routes
  // ---------------------------------------------------------------------------

  static routeForRootCampaign(campaignId: string): string {
    return `/campaigns/${campaignId}`;
  }

  static routeForManuallyAddingLeads(campaignId: string): string {
    return `/campaigns/${campaignId}/add-leads`;
  }

  static routeForReviewLeads(
    campaignId: string,
    reviewLeadsId: string,
  ): string {
    return `${LinkRouter.routeForRootCampaign(campaignId)}/reviewLeads/${reviewLeadsId}`;
  }

  static routeForProcessing(campaignId: string): string {
    return `${LinkRouter.routeForRootCampaign(campaignId)}/processing`;
  }

  static routeForLaunching(campaignId: string): string {
    return `${LinkRouter.routeForRootCampaign(campaignId)}/launching`;
  }

  static routeForCampaignEdit(campaignId: string): string {
    return `${LinkRouter.routeForRootCampaign(campaignId)}/edit`;
  }

  static routeForCampaignEditPitch(campaignId: string): string {
    return `${LinkRouter.routeForRootCampaign(campaignId)}/edit-pitch`;
  }

  static routeForCampaignEditPersona(campaignId: string): string {
    return `${LinkRouter.routeForRootCampaign(campaignId)}/edit-persona`;
  }

  static routeForCampaignEditIcp(campaignId: string): string {
    return `${LinkRouter.routeForRootCampaign(campaignId)}/edit-icp`;
  }

  // ---------------------------------------------------------------------------
  // Organization Routes
  // ---------------------------------------------------------------------------

  static getRootOrganizationRoute(organizationName: string): string {
    return `/organization/${organizationName}`;
  }

  static routeForOrganizationHome(
    organizationName: string,
    opts?: { tab?: 'campaigns' | 'reps' },
  ): string {
    const base = `/organization/${organizationName}/home`;
    if (!opts?.tab) return base;
    const params = new URLSearchParams({ tab: opts.tab });
    return `${base}?${params.toString()}`;
  }

  static routeForOrganizationPersonalPage(organizationName: string): string {
    return `/organization/${organizationName}/personal`;
  }

  static routeForOrganizationInbox(
    organizationSlug: string,
    filters?: {
      tier?: 'auto' | 'optional' | 'needs_input' | null;
      status?: 'all' | 'priority' | 'booked' | 'paused';
      // Multi-select campaign bases + reps, each serialized as one CSV param.
      base?: string[]; // campaignBaseIds
      reps?: string[]; // selected rep (user) ids
      showInactive?: boolean; // show inactive-campaign (PAUSED-stage) leads
      // Paused leads are shown by default now; the URL only carries the
      // non-default OFF state (`showPaused=0`). Undefined = leave it default (on).
      showPaused?: boolean; // false ⇒ hide paused leads
      // List sort mode. Omitted when the default ('needs_attention'); callers pass
      // undefined for the default so shared URLs stay clean.
      sort?: 'needs_attention' | 'recent_activity' | 'stage';
      q?: string; // search
      c?: string | null; // selected conversation responseId (incl. `lead:<id>`)
      r?: string | null; // permalinked GeneratedChatResponse id (specific GCR)
      settings?: 'notifications';
    },
  ): string {
    const path = `/organization/${organizationSlug}/inbox`;
    if (!filters) return path;
    // Omit default/empty values so shared URLs stay clean.
    const params = new URLSearchParams();
    if (filters.tier) params.set('tier', filters.tier);
    if (filters.status && filters.status !== 'all') {
      params.set('status', filters.status);
    }
    if (filters.base && filters.base.length > 0) {
      params.set('base', filters.base.join(','));
    }
    if (filters.reps && filters.reps.length > 0) {
      params.set('reps', filters.reps.join(','));
    }
    if (filters.showInactive) params.set('showInactive', '1');
    // Paused leads default ON; only serialize the explicit OFF state.
    if (filters.showPaused === false) params.set('showPaused', '0');
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.q?.trim()) params.set('q', filters.q.trim());
    if (filters.c) params.set('c', filters.c);
    if (filters.r) params.set('r', filters.r);
    if (filters.settings) params.set('settings', filters.settings);
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }

  static routeForOrganizationTeamLeads(organizationName: string): string {
    return `/organization/${organizationName}/teamLeads`;
  }

  static routeForOrganizationNewCampaign(
    organizationName: string,
    duplicatingCampaignId?: string,
  ): string {
    const base = `/organization/${organizationName}/newCampaign`;
    if (!duplicatingCampaignId) return base;
    const params = new URLSearchParams({ duplicatingCampaignId });
    return `${base}?${params.toString()}`;
  }

  static routeForCampaignBaseEdit(
    organizationName: string,
    campaignBaseId: string,
  ): string {
    return `/organization/${organizationName}/campaign-base/${campaignBaseId}`;
  }

  static routeForCampaignBaseLaunch(
    orgSlug: string,
    campaignBaseId: string,
  ): string {
    return `/organization/${orgSlug}/bases/${campaignBaseId}/launch`;
  }

  /**
   * Guard a `?returnUrl=` (or any user-influenced redirect target) against open
   * redirects: only follow same-origin relative paths. Rejects absolute URLs
   * (`http(s):`), protocol-relative (`//host`), and backslash tricks (`/\\host`),
   * falling back to `fallback`.
   */
  static safeReturnUrl(
    returnUrl: string | undefined | null,
    fallback: string,
  ): string {
    if (!returnUrl) return fallback;
    const isInternalPath =
      returnUrl.startsWith('/') &&
      !returnUrl.startsWith('//') &&
      !returnUrl.startsWith('/\\');
    return isInternalPath ? returnUrl : fallback;
  }

  static routeForCampaignBaseEditPitch(
    organizationName: string,
    campaignBaseId: string,
  ): string {
    return `/organization/${organizationName}/campaign-base/${campaignBaseId}/edit-pitch`;
  }

  static routeForCampaignBaseEditIcp(
    organizationName: string,
    campaignBaseId: string,
  ): string {
    return `/organization/${organizationName}/campaign-base/${campaignBaseId}/edit-icp`;
  }

  static routeForCampaignBaseEditPersona(
    organizationName: string,
    campaignBaseId: string,
  ): string {
    return `/organization/${organizationName}/campaign-base/${campaignBaseId}/edit-persona`;
  }

  static routeForOrganizationPostManager(organizationName: string): string {
    return `/organization/${organizationName}/postManager`;
  }

  static routeForOrganizationPipeline(organizationName: string): string {
    return `/organization/${organizationName}/team-dashboard`;
  }

  static routeForAnalytics(
    organizationSlug: string,
    query?: URLSearchParams,
  ): string {
    const path = `/organization/${organizationSlug}/analytics`;
    const qs = query?.toString();
    return qs ? `${path}?${qs}` : path;
  }

  static routeForPersonalAnalytics(query?: URLSearchParams): string {
    const path = '/analytics';
    const qs = query?.toString();
    return qs ? `${path}?${qs}` : path;
  }

  static routeForOrganizationCallsCalendar(organizationSlug: string): string {
    return `/organization/${organizationSlug}/calls`;
  }

  static routeForCrmSettings(organizationSlug: string): string {
    return `/organization/${organizationSlug}/crm-settings`;
  }

  static routeForHubspotOAuthCallback(): string {
    return '/api/crm/hubspot/callback';
  }

  static routeForCrmRecords(organizationSlug: string): string {
    return `/organization/${organizationSlug}/crm-records`;
  }

  static routeForCompanyLists(organizationSlug: string): string {
    return `/organization/${organizationSlug}/company-lists`;
  }

  // Teams: roster surfaces as a tab on the team-dashboard (the home page),
  // not as a separate route. Teams management lives inside the org-customized
  // profile button via TeamManagementPage.

  // ---------------------------------------------------------------------------
  // Welcome / Onboarding Routes
  // ---------------------------------------------------------------------------

  static getWelcomeRouteForOrganization(orgName: string): string {
    return `/welcome/organizations/${orgName}`;
  }

  static getWelcomeSetupRouteForOrganization(orgName: string): string {
    return `/welcome/organizations/${orgName}/setup`;
  }

  // ---------------------------------------------------------------------------
  // Employee Routes (relative paths for frontend navigation within employee deployment)
  // ---------------------------------------------------------------------------

  /**
   * @deprecated Misnamed — points at `/employee/individual-chat` (a
   * different page from the messaging-inbox-based `absoluteEmployeeChat`,
   * which is the runtime footgun). Use `routeForEmployeeIndividualChat`
   * for clarity, or `routeForEmployeeMessagingInbox` for the inbox view.
   */
  static routeForEmployeeChat(leadId: string): string {
    return LinkRouter.routeForEmployeeIndividualChat(leadId);
  }

  static routeForEmployeeIndividualChat(
    leadId: string,
    options?: { openMeetings?: boolean },
  ): string {
    const params = new URLSearchParams({ leadId });
    // Deep-link straight into the Meetings modal (booked-meeting flow).
    if (options?.openMeetings) params.set('openMeetings', '1');
    return `/employee/individual-chat?${params.toString()}`;
  }

  static routeForEmployeeMessagingInbox(leadId: string): string {
    const params = new URLSearchParams({ leadId });
    return `/employee/messaging-inbox?${params.toString()}`;
  }

  /**
   * Deep-link to the bulk conversation viewer (`/employee/conversations`).
   * Accepts either an explicit `leadIds` list OR a scope filter
   * (org / user / campaign + stage / responseStage / date). `leadId`
   * pre-selects a conversation so the exact view is shareable. Mirrors the
   * scope shape of the `bulk_get_conversations` MCP tool so a tool can build
   * a viewer link straight from the same args it queried with.
   */
  static routeForEmployeeConversations(
    params: EmployeeConversationsParams,
  ): string {
    const search = new URLSearchParams();
    if (params.analysis)
      search.set('analysis', JSON.stringify(params.analysis));
    const setList = (key: string, values?: string[]) => {
      if (values && values.length > 0) search.set(key, values.join(','));
    };
    setList('leadIds', params.leadIds);
    setList('campaignIds', params.campaignIds);
    setList('orgIds', params.orgIds);
    setList('userIds', params.userIds);
    setList('stages', params.stages);
    setList('campaignStages', params.campaignStages);
    setList('responseStages', params.responseStages);

    // Per-field date windows (queuedFrom/queuedTo, bookedFrom/bookedTo, …).
    // One window per field: explicit `dateRanges` win; legacy since/until only
    // fill "updated" if not already set, so the two inputs can't conflict.
    const byField = new Map<string, ConversationDateRange>();
    for (const range of params.dateRanges ?? [])
      byField.set(range.field, range);
    if ((params.since || params.until) && !byField.has('updated')) {
      byField.set('updated', {
        field: 'updated',
        from: params.since,
        to: params.until,
      });
    }
    for (const field of CONVERSATION_DATE_FIELDS) {
      const range = byField.get(field);
      if (range?.from) search.set(dateFromParam(field), range.from);
      if (range?.to) search.set(dateToParam(field), range.to);
    }

    if (params.leadId) search.set('leadId', params.leadId);
    const qs = search.toString();
    return `/employee/conversations${qs ? `?${qs}` : ''}`;
  }

  /**
   * Deep-link to the messaging-inbox **Tasks tab**, optionally pre-filtered by
   * a single scope (assignee / campaign / organization). Use this for
   * "show this user's intervention tasks" links in MCP / Slack / emails —
   * `/tasks` (the customer-facing page) only shows the viewer's own tasks
   * and ignores scope params, so it can't deep-link an admin to someone
   * else's tasks.
   */
  static routeForEmployeeMessagingInboxTasks(params?: {
    assigneeId?: string;
    campaignId?: string;
    organizationId?: string;
    taskStatus?: string;
    leadId?: string;
  }): string {
    const search = new URLSearchParams({ tab: 'tasks' });
    if (params?.assigneeId) search.set('assigneeId', params.assigneeId);
    if (params?.campaignId) search.set('campaignId', params.campaignId);
    if (params?.organizationId)
      search.set('organizationId', params.organizationId);
    if (params?.taskStatus) search.set('taskStatus', params.taskStatus);
    if (params?.leadId) search.set('leadId', params.leadId);
    return `/employee/messaging-inbox?${search.toString()}`;
  }

  /**
   * Builds the relative path for an employee page from its registry slug.
   * Single generic helper for the static, param-less employee namespace driven
   * by the employee page registry (see employeePageRegistry.ts) — used by the
   * navbar and Cmd+K. Parameterized routes keep their dedicated helpers below
   * (e.g. routeForEmployeeCampaigns, routeForEmployeeLeadpool).
   */
  static routeForEmployeePage(slug: string): string {
    return slug === 'index' ? '/employee' : `/employee/${slug}`;
  }

  static routeForEmployeePostManager(): string {
    return '/employee/postManager';
  }

  /**
   * Generates a URL that, when visited, auto-creates a LinkedIn post.
   * Content is base64-encoded in the hash fragment to avoid URL length limits.
   */
  static routeForEmployeePostManagerCreatePost(params: {
    userId?: string;
    orgId?: string;
    publisherId: string;
    content: string;
  }): string {
    const searchParams = new URLSearchParams();
    if (params.userId) searchParams.set('userId', params.userId);
    if (params.orgId) searchParams.set('orgId', params.orgId);
    searchParams.set('publisherId', params.publisherId);
    searchParams.set('createPost', '1');
    const encodedContent = btoa(encodeURIComponent(params.content));
    return `/employee/postManager/accountPage?${searchParams.toString()}#content=${encodedContent}`;
  }

  static routeForEmployeeLinkedinHealth(): string {
    return '/employee/dashboards/linkedin-account-health';
  }

  static routeForEmployeeIndividualLinkedinHealth(userId: string): string {
    const params = new URLSearchParams({ userId });
    return `/employee/dashboards/linkedin-account-health/individual?${params.toString()}`;
  }

  static routeForEmployeeExternalSlackInbox(channelId?: string): string {
    if (!channelId) return '/employee/external-slack-inbox';
    const params = new URLSearchParams({ channelId });
    return `/employee/external-slack-inbox?${params.toString()}`;
  }

  static routeForEmployeeCustomers(customerId?: string): string {
    if (!customerId) return '/employee/customers';
    // The page selects by `customerId` in the strict `org:{id}` / `user:{id}` form
    // (parseCustomerId throws otherwise). Callers commonly pass a bare org id —
    // normalize it to the org form so the link opens that org's page instead of
    // erroring on the list.
    const normalized = customerId.includes(':')
      ? customerId
      : `org:${customerId}`;
    const params = new URLSearchParams({ customerId: normalized });
    return `/employee/customers?${params.toString()}`;
  }

  /** The org's response-policy templates manager (needs an organizationId). */
  static routeForResponsePolicyTemplates(
    organizationId: string,
    templateId?: string | null,
  ): string {
    const params = new URLSearchParams({ organizationId });
    if (templateId) params.set('templateId', templateId);
    return `/employee/response-policy-templates?${params.toString()}`;
  }

  static routeForEmployeeCampaignBases(
    baseIdOrFilters?:
      | string
      | { search?: string; organizationId?: string; stages?: string[] },
    tab?: string,
  ): string {
    if (!baseIdOrFilters) return '/employee/campaign-bases';
    // Legacy signature: bare string = baseId (detail view). `tab` deep-links to
    // a tab on that base (e.g. the in-chat "Manage full policy" link).
    if (typeof baseIdOrFilters === 'string') {
      const params = new URLSearchParams({ baseId: baseIdOrFilters });
      if (tab) params.set('tab', tab);
      return `/employee/campaign-bases?${params.toString()}`;
    }
    // Object form: list-view filters.
    const params = new URLSearchParams();
    if (baseIdOrFilters.search) params.set('search', baseIdOrFilters.search);
    if (baseIdOrFilters.organizationId)
      params.set('organizationId', baseIdOrFilters.organizationId);
    if (baseIdOrFilters.stages?.length)
      params.set('stages', baseIdOrFilters.stages.join(','));
    const qs = params.toString();
    return qs ? `/employee/campaign-bases?${qs}` : '/employee/campaign-bases';
  }

  static routeForEmployeeFeatureFlags(): string {
    return '/employee/feature-flags';
  }

  static routeForEmployeeAuditInbox(): string {
    return '/employee/audit-inbox';
  }

  /**
   * Audit inbox with pre-applied filters. dateFrom / dateTo accept either
   * YYYY-MM-DD or a full ISO timestamp (e.g. "2026-05-27T10:00:00.000Z") —
   * the page passes them straight through to the tRPC query which casts to
   * ::timestamptz, so sub-day windows work end-to-end.
   */
  static routeForEmployeeAuditInboxFiltered(filters: {
    dateFrom?: string;
    dateTo?: string;
    senderClerkUserIds?: string[];
    organizationIds?: string[];
    userIds?: string[];
    campaignIds?: string[];
    campaignStages?: string[];
    userStatus?: 'active' | 'inactive' | 'all';
    sortOrder?: 'asc' | 'desc';
  }): string {
    const params = new URLSearchParams();
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.senderClerkUserIds?.length)
      params.set('senderClerkUserIds', filters.senderClerkUserIds.join(','));
    if (filters.organizationIds?.length)
      params.set('organizationIds', filters.organizationIds.join(','));
    if (filters.userIds?.length)
      params.set('userIds', filters.userIds.join(','));
    if (filters.campaignIds?.length)
      params.set('campaignIds', filters.campaignIds.join(','));
    if (filters.campaignStages?.length)
      params.set('campaignStages', filters.campaignStages.join(','));
    if (filters.userStatus && filters.userStatus !== 'all')
      params.set('userStatus', filters.userStatus);
    if (filters.sortOrder && filters.sortOrder !== 'desc')
      params.set('sortOrder', filters.sortOrder);
    const qs = params.toString();
    return qs ? `/employee/audit-inbox?${qs}` : '/employee/audit-inbox';
  }

  static routeForEmployeeBumpingInbox(): string {
    return '/employee/bumping-inbox';
  }

  static routeForEmployeeLeadgenRuns(): string {
    return '/employee/utils/leadgen-runs';
  }

  static routeForEmployeeReignitionAnalytics(scope?: {
    organizationId?: string;
    campaignBaseId?: string;
    campaignId?: string;
  }): string {
    const params = new URLSearchParams();
    if (scope?.organizationId)
      params.set('organizationId', scope.organizationId);
    if (scope?.campaignBaseId)
      params.set('campaignBaseId', scope.campaignBaseId);
    if (scope?.campaignId) params.set('campaignId', scope.campaignId);
    const query = params.toString();
    return query
      ? `/employee/dashboards/reignition-analytics?${query}`
      : '/employee/dashboards/reignition-analytics';
  }

  /**
   * Lead Reignition home is the campaign-base detail tab. Without a base id,
   * fall back to the Utils picker (which redirects here once a base is chosen).
   * List tab uses `reignitionTab` so it does not collide with the base `tab`.
   */
  static routeForEmployeeLeadReignition(scope?: {
    campaignBaseId?: string;
    userId?: string;
    campaignId?: string;
    tab?: 'requested' | 'exiting' | 'exited' | 'flow' | 'history';
  }): string {
    if (!scope?.campaignBaseId) {
      return '/employee/utils/lead-reignition';
    }
    const params = new URLSearchParams({
      baseId: scope.campaignBaseId,
      tab: 'leadReignition',
    });
    if (scope.userId) params.set('userId', scope.userId);
    if (scope.campaignId) params.set('campaignId', scope.campaignId);
    if (scope.tab && scope.tab !== 'requested') {
      params.set('reignitionTab', scope.tab);
    }
    return `/employee/campaign-bases?${params.toString()}`;
  }

  /** Scoped fallback for employees who only have the reignition utility grant. */
  static routeForEmployeeLeadReignitionUtility(scope?: {
    campaignBaseId?: string;
    userId?: string;
    campaignId?: string;
    tab?: 'requested' | 'exiting' | 'exited' | 'flow' | 'history';
  }): string {
    const params = new URLSearchParams();
    if (scope?.campaignBaseId)
      params.set('campaignBaseId', scope.campaignBaseId);
    if (scope?.userId) params.set('userId', scope.userId);
    if (scope?.campaignId) params.set('campaignId', scope.campaignId);
    if (scope?.tab && scope.tab !== 'requested') params.set('tab', scope.tab);
    const query = params.toString();
    return `/employee/utils/lead-reignition${query ? `?${query}` : ''}`;
  }

  static routeForEmployeeCampaignBaseLeads(scope?: {
    campaignBaseId?: string;
    campaignId?: string;
    tab?: 'reportSources' | 'queued';
    /** Deep-link to a specific highlighted CampaignBaseLead (opens its detail
     * panel on load) — for sharing + debugging a single candidate. */
    activeLeadId?: string;
  }): string {
    const params = new URLSearchParams();
    if (scope?.campaignBaseId)
      params.set('campaignBaseId', scope.campaignBaseId);
    if (scope?.campaignId) params.set('campaignId', scope.campaignId);
    if (scope?.activeLeadId) params.set('lead', scope.activeLeadId);
    if (scope?.tab) params.set('tab', scope.tab);
    const query = params.toString();
    return query
      ? `/employee/utils/campaign-base-leads?${query}`
      : '/employee/utils/campaign-base-leads';
  }

  /** The ranking-eval workbench. `anchors` loads the base's frozen anchor set
   * as the population (base-scoped, so it takes no campaignId). */
  static routeForEmployeeRankingEval(scope?: {
    campaignBaseId?: string;
    campaignId?: string;
    anchors?: boolean;
  }): string {
    const params = new URLSearchParams();
    if (scope?.campaignBaseId) params.set('baseId', scope.campaignBaseId);
    if (scope?.campaignId) params.set('campaignId', scope.campaignId);
    if (scope?.anchors) params.set('anchors', '1');
    const query = params.toString();
    return query
      ? `/employee/utils/ranking-eval?${query}`
      : '/employee/utils/ranking-eval';
  }

  static routeForEmployeeLeadUpdateAudit(scope?: {
    leadUpdateId?: string;
    leadId?: string;
  }): string {
    const params = new URLSearchParams();
    if (scope?.leadUpdateId) params.set('leadUpdateId', scope.leadUpdateId);
    if (scope?.leadId) params.set('leadId', scope.leadId);
    const query = params.toString();
    return query
      ? `/employee/utils/lead-update-audit?${query}`
      : '/employee/utils/lead-update-audit';
  }

  static routeForEmployeeDripMessageTagging(campaignId?: string): string {
    if (!campaignId) return '/employee/drip-message-tagging';
    const params = new URLSearchParams({ campaignId });
    return `/employee/drip-message-tagging?${params.toString()}`;
  }

  static routeForEmployeeCompareMessagingStrategies(): string {
    return '/employee/compare-messaging-strategies';
  }

  static routeForEmployeeWeeklyStats(params?: {
    campaignId?: string;
    userId?: string;
    organizationId?: string;
  }): string {
    const searchParams = new URLSearchParams();
    if (params?.campaignId) searchParams.set('campaignId', params.campaignId);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.organizationId)
      searchParams.set('organizationId', params.organizationId);
    const qs = searchParams.toString();
    return qs ? `/employee/weekly-stats?${qs}` : '/employee/weekly-stats';
  }

  static routeForEmployeeCampaigns(campaignId: string): string {
    const params = new URLSearchParams({ campaignId });
    return `/employee/campaigns?${params.toString()}`;
  }

  static routeForEmployeeCampaignOperations(
    campaignId: string,
    options?: { remind?: 'slack' | 'email' | 'all' },
  ): string {
    const params = new URLSearchParams({ campaignId, tab: 'operations' });
    if (options?.remind) params.set('remind', options.remind);
    return `/employee/campaigns?${params.toString()}`;
  }

  /** Deep-links to the campaign viewer with the changelog tab pre-selected. */
  static routeForEmployeeCampaignChangelog(campaignId: string): string {
    const params = new URLSearchParams({ campaignId, tab: 'changelog' });
    return `/employee/campaigns?${params.toString()}`;
  }

  /** Deep-links to the campaign viewer's response-policy tab (campaign -> base chain). */
  static routeForEmployeeCampaignResponsePolicy(campaignId: string): string {
    const params = new URLSearchParams({ campaignId, tab: 'responsePolicy' });
    return `/employee/campaigns?${params.toString()}`;
  }

  /** Index view of the employee campaigns page (no specific campaign). */
  static routeForEmployeeCampaignsIndex(): string {
    return '/employee/campaigns';
  }

  static routeForEmployeeLeadpool(campaignId?: string): string {
    if (!campaignId) return '/employee/leadpool';
    const params = new URLSearchParams({ campaignId });
    return `/employee/leadpool?${params.toString()}`;
  }

  static routeForEmployeeUserDetail(
    userId: string,
    options?: { tab?: string },
  ): string {
    if (!options?.tab) return `/employee/users/${userId}`;
    const params = new URLSearchParams({ tab: options.tab });
    return `/employee/users/${userId}?${params.toString()}`;
  }

  static routeForEmployeeResponseGenEval(options?: {
    useReadOnly?: boolean;
  }): string {
    return LinkRouter.withEvalDbFlag(
      '/employee/response-gen-eval',
      options?.useReadOnly,
    );
  }

  /**
   * Detail page for an EVAL batch — keyed by the batch's grouping UUID.
   */
  static routeForEmployeeEvalBatchDetail(
    batchId: string,
    options?: { useReadOnly?: boolean },
  ): string {
    return LinkRouter.withEvalDbFlag(
      `/employee/response-gen-eval/batch/${batchId}`,
      options?.useReadOnly,
    );
  }

  /**
   * Detail page for a LIVE fan-out group — keyed by the parent
   * `GeneratedChatResponse.id`. All variant runs for that response (priority +
   * shadows) render there side-by-side.
   */
  static routeForEmployeeEvalLiveGroupDetail(
    generatedChatResponseId: string,
    options?: { useReadOnly?: boolean },
  ): string {
    return LinkRouter.withEvalDbFlag(
      `/employee/response-gen-eval/live/${generatedChatResponseId}`,
      options?.useReadOnly,
    );
  }

  /**
   * The eval dashboard's "DB: prod" toggle lives in a `?db=prod` query
   * param so pasted URLs preserve the source. Navigation between eval pages
   * needs the flag propagated, otherwise a click on "DB: prod" can silently
   * land on a page reading local data.
   */
  private static withEvalDbFlag(
    path: string,
    useReadOnly: boolean | undefined,
  ): string {
    if (!useReadOnly) return path;
    const params = new URLSearchParams({ db: 'prod' });
    return `${path}?${params.toString()}`;
  }
}
